import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {captureMessage} from "@sentry/react";
import {useFormikContext} from "formik";
import translit from "ua-en-translit";

import {getCurrentUser} from "../../slices/UserSlice";
import FunctionButton from "../../utilities/Buttons/FunctionButton";
import IconButton from "../../utilities/Buttons/IconButton";
import {ICON_NAMES} from "../../utilities/generalConstants.js/generalConstants";
import {swalCustom} from "../../utilities/hooks/swalCustom";
import {toaster} from "../../utilities/hooks/toaster";
import {UI_PERMISSION_TYPE} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";
import {TextAreaInput} from "../Inputs";
import {ButtonSelect} from "../Inputs/ButtonSelect";
import PdfFilePreview from "../Inputs/fileSelect/preview/PdfPreview";
import {DEV_TICKETS_LIST_WINDOW_NAME} from "../Reference/DevTicketsList/DevTicketsList";

import {
  addNewTicket,
  addNewTicketFile,
  clearCurrentTicket,
  deleteTicket,
  deleteTicketFile,
  fetchAllTickets,
  fetchDevITFilters,
  fetchNewTickets,
  fetchTicket,
  selectAllDevITTicketTypes,
  selectDevITLoading,
  selectDevITTicket,
  updateTicket,
} from "./DevITSlice";

const DevITForm = ({files, page, currentTicketId, setCurrentTicketId, fileArray}) => {
  const {
    t,
    i18n: {language},
  } = useTranslation();
  const dispatch = useDispatch();
  const {initialValues, values, setValues} = useFormikContext();

  const {ticketTypeId, ticketText} = values || {};
  const loading = useSelector(selectDevITLoading);
  const filters = JSON.parse(sessionStorage.getItem(DEV_TICKETS_LIST_WINDOW_NAME));
  const noTicketData = !values?.ticketText || !values?.ticketTypeId;
  const currentTicket = useSelector(selectDevITTicket);
  const ticketTypes = useSelector(selectAllDevITTicketTypes);
  const currentUser = useSelector(getCurrentUser);

  const ticketTypesOptions =
    ticketTypes?.map(({typeId, nameEng, nameUkr}) => ({value: typeId, title: language === "en" ? nameEng : nameUkr})) ??
    [];

  const isCurrentTicketInWork = currentTicket?.status?.statusId === 2;
  const isCurrentTicketClosed = currentTicket?.status?.statusId === 3;
  const hasTicketTextChanged = currentTicket?.text !== ticketText && ticketText;
  const hasTicketTypeChanged = currentTicket?.typeId !== ticketTypeId && ticketTypeId;
  const isSaveBtnDisabled =
    isCurrentTicketInWork ||
    isCurrentTicketClosed ||
    (!currentTicketId && (!ticketTypeId || !ticketText)) ||
    (currentTicketId && !(hasTicketTextChanged || hasTicketTypeChanged));
  const isDeleteBtnDisabled =
    currentUser?.id !== currentTicket?.creatorId ||
    !currentTicketId ||
    isCurrentTicketClosed ||
    isCurrentTicketInWork ||
    fileArray?.length > 0;

  useEffect(() => {
    if (currentTicketId) {
      dispatch(fetchTicket({ticketId: currentTicketId}));
    }
  }, [currentTicketId]);

  useEffect(() => {
    if (currentTicket?.ticketId) {
      setValues(prev => ({
        ...prev,
        itNote: currentTicket?.note,
        ticketText: currentTicket?.text,
        ticketTypeId: currentTicket?.typeId,
      }));
    } else {
      setCurrentTicketId(null);
      setValues(initialValues);
    }
  }, [currentTicket?.ticketId]);

  const formSentryTicket = ticket => {
    if (!ticket) return {};
    const {ticketId, text, type, creator} = ticket;

    const ticketData = {
      ticketId,
      text,
      type: language === "en" ? type?.nameEng : type?.nameUkr,
      creator: creator?.name,
    };

    const formattedMessage =
      `🎫 <b>New DevIT Ticket!</b>\n\n` +
      `Тикет #${ticketData.ticketId} [${ticketData.type}]\n` +
      `Создатель: ${ticketData.creator}\n` +
      `Текст: ${ticketData.text}`;

    return {ticketData, formattedMessage};
  };

  const onAddTicketFile = arrFiles => {
    if (!currentTicketId) return;
    const formdata = new FormData();
    let file = arrFiles[0];
    let filename = translit(file.name);
    formdata.append("file", file, filename);

    if (formdata.has("file")) {
      dispatch(addNewTicketFile({ticketId: currentTicketId, body: formdata})).then(resp => {
        if (resp.meta.requestStatus === "fulfilled") {
          toaster.success("Ticket file added");
        }
      });
    }
  };

  const onDeleteTicketFile = fileId => {
    if (!currentTicketId) return;
    dispatch(deleteTicketFile({fileId})).then(resp => {
      if (resp.meta.requestStatus === "fulfilled") {
        toaster.success("Ticket file deleted");
      }
    });
  };

  const onSaveBtnClick = () => {
    //save
    if (!currentTicketId) {
      if (!ticketTypeId || !ticketText) return;
      dispatch(addNewTicket({body: {typeId: ticketTypeId, text: ticketText}})).then(resp => {
        if (resp.meta.requestStatus === "fulfilled") {
          toaster.success("New ticket added");
          setCurrentTicketId(resp.payload.ticketId);
          dispatch(fetchDevITFilters(filters));
          dispatch(fetchNewTickets())
            .unwrap()
            .catch(e => console.error(e));

          const {ticketData, formattedMessage} = formSentryTicket(resp.payload);

          captureMessage(formattedMessage, {
            level: "warning",
            extra: {rawData: ticketData},
          });
        }
      });
    }
    //edit
    if (currentTicketId) {
      dispatch(updateTicket({ticketId: currentTicketId, body: {typeId: ticketTypeId, text: ticketText}}))
        .then(resp => {
          if (resp.meta.requestStatus === "fulfilled") {
            toaster.success("Ticket updated");
          }
        })
        .catch(e => console.error(e));
    }
  };

  const onDeleteBtnClick = () => {
    if (!currentTicketId) return;
    swalCustom.confirm({
      confirmFunc: () => {
        dispatch(deleteTicket({ticketId: currentTicketId}))
          .then(resp => {
            if (resp.meta.requestStatus === "fulfilled") {
              toaster.success("Ticket deleted");
              dispatch(fetchDevITFilters(filters));
              dispatch(clearCurrentTicket());
              dispatch(fetchNewTickets())
                .unwrap()
                .catch(e => console.error(e));
              setCurrentTicketId(null);
              setValues(initialValues);
            }
          })
          .catch(e => console.error(e));
      },
    });
  };

  const onCreateNewAppealBtnClick = () => {
    if (!currentTicketId) {
      dispatch(addNewTicket({typeId: ticketTypeId, text: ticketText}))
        .then(resp => {
          if (resp.ticketId) {
            toaster.success("New ticket added");
            dispatch(fetchAllTickets({...filters, page: page?.currentPage}))
              .unwrap()
              .catch(e => {
                console.error(e);
              });
            dispatch(fetchNewTickets())
              .unwrap()
              .catch(e => console.error(e));
          }
        })
        .catch(e => console.error(e));
    }
    if (currentTicketId && (currentTicket?.typeId !== ticketTypeId || currentTicket?.text !== ticketText)) {
      dispatch(updateTicket({ticketId: currentTicketId, body: {typeId: ticketTypeId, text: ticketText}}))
        .unwrap()
        .then(resp => {
          if (resp.ticketId) {
            toaster.success("Ticket updated");
            dispatch(fetchAllTickets({...filters, page: page?.currentPage}))
              .unwrap()
              .catch(e => console.error(e));
          }
        });
    }
    setCurrentTicketId(null);
    dispatch(clearCurrentTicket());
    setValues(initialValues);
  };

  return (
    <div className="w100 row">
      <div className="fGrow w100">
        <div className="row w100 aibl jcfs gap2x">
          <FunctionButton
            name="Create new appeal"
            onClick={onCreateNewAppealBtnClick}
            autoWidth
            disabled={noTicketData}
            tooltipMessage={noTicketData ? "" : "createNewAppealMsg"}
            uiPermissionType={UI_PERMISSION_TYPE.READABLE}
          />
          <ButtonSelect
            label="Add appeal type"
            name="ticketTypeId"
            options={ticketTypesOptions}
            disabled={isCurrentTicketInWork || isCurrentTicketClosed}
            selectOnly
            uiPermissionType={UI_PERMISSION_TYPE.READABLE}
          />
        </div>
        <TextAreaInput
          name="ticketText"
          height="7rem"
          placeholder={t("Enter appeal text (5000 chars with spaces)")}
          disabled={isCurrentTicketInWork || isCurrentTicketClosed}
          uiPermissionType={UI_PERMISSION_TYPE.READABLE}
        />
      </div>
      <div className="pt5 fcCol gap aic jcsb">
        <PdfFilePreview
          files={files}
          delFunc={onDeleteTicketFile}
          addFunc={onAddTicketFile}
          disabled={!currentTicketId || isCurrentTicketClosed}
          loading={loading}
          accept={".jpg,.jpeg,.png,.gif,.webp"}
          uiPermissionType={UI_PERMISSION_TYPE.READABLE}
        />
        <IconButton
          nameIcon={ICON_NAMES.SAVE}
          onClick={onSaveBtnClick}
          disabled={isSaveBtnDisabled}
          ariaLabel="Save"
          uiPermissionType={UI_PERMISSION_TYPE.READABLE}
        />
        <IconButton
          nameIcon={ICON_NAMES.TRASH}
          onClick={onDeleteBtnClick}
          tooltipMessage={fileArray?.length > 0 ? "Delete files first" : ""}
          disabled={isDeleteBtnDisabled}
          ariaLabel="Delete"
          uiPermissionType={UI_PERMISSION_TYPE.READABLE}
        />
      </div>
    </div>
  );
};

export default DevITForm;
